import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { initials } from '../components/TournamentCard';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AV_GRADIENTS = [
  'linear-gradient(135deg,#F5A623,#DD8A0A)',
  'linear-gradient(135deg,#0A8FA8,#066D81)',
  'linear-gradient(135deg,#E5503E,#C23B2A)',
  'linear-gradient(135deg,#6BAA1E,#4E8012)',
  'linear-gradient(135deg,#9B59B6,#7D3C98)',
  'linear-gradient(135deg,#3498DB,#1A6EA8)',
];
function gradFor(id) {
  let h = 0;
  for (const c of String(id || '0')) h = (h * 31 + c.charCodeAt(0)) & 0xFFFFFF;
  return AV_GRADIENTS[Math.abs(h) % AV_GRADIENTS.length];
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60) return 'À l\'instant';
  if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 172800) return 'Hier';
  return `Il y a ${Math.floor(diff / 86400)} j`;
}

function NotifAvatar({ user }) {
  const grad = gradFor(user?.id);
  const ini = initials(user?.firstName, user?.lastName);
  if (user?.avatarUrl) {
    return (
      <div style={{ width: 44, height: 44, borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
        <img src={user.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    );
  }
  return (
    <div style={{
      width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
      background: grad, display: 'flex', alignItems: 'center',
      justifyContent: 'center', color: '#fff', fontSize: 15, fontWeight: 700,
    }}>{ini}</div>
  );
}

export default function Notifications({ user }) {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const fetchNotifs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users/me/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifs(res.data);
    } catch {}
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchNotifs(); }, [fetchNotifs]);

  const act = async (notifId, action) => {
    setActing(notifId);
    try {
      await axios.post(`${API_URL}/api/notifications/${notifId}/${action}`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      await fetchNotifs();
    } catch {}
    finally { setActing(null); }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API_URL}/api/users/me/notifications/read-all`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      await fetchNotifs();
    } catch {}
  };

  const pendingNotifs = notifs.filter(n => n.status === 'pending');
  const pastNotifs = notifs.filter(n => n.status !== 'pending');

  return (
    <div className="screen" style={{ background: 'var(--ink)', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px 12px', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div className="display" style={{ fontSize: 26 }}>Notifications</div>
            {pendingNotifs.length > 0 && (
              <div className="mono" style={{ color: 'var(--sun-2)', fontSize: 11, marginTop: 2 }}>
                {pendingNotifs.length} en attente
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
            {pendingNotifs.length > 0 && (
              <button onClick={markAllRead} style={{
                padding: '7px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                background: 'var(--ink-3)', color: 'var(--text-2)', border: 'none', cursor: 'pointer',
              }}>
                Tout marquer lu
              </button>
            )}
            <button aria-label="Retour" onClick={() => navigate(-1)} style={{
              width: 36, height: 36, borderRadius: '50%', background: 'var(--ink-3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: 'none', cursor: 'pointer', color: 'var(--text)',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="page-content" style={{ paddingBottom: 40 }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-3)' }}>
            <span className="mono">Chargement…</span>
          </div>
        )}

        {!loading && notifs.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔔</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>Tout est à jour</div>
            <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Aucune notification en attente.</div>
          </div>
        )}

        {/* Nouveau — pending */}
        {!loading && pendingNotifs.length > 0 && (
          <div style={{ padding: '0 20px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', color: 'var(--sun-2)', marginBottom: 10 }}>
              Nouveau
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {pendingNotifs.map(n => (
                <NotifCard key={n.id} notif={n} acting={acting} onAct={act} onProfile={(id) => navigate(`/profil/${id}`)} />
              ))}
            </div>
          </div>
        )}

        {/* Plus tôt — read */}
        {!loading && pastNotifs.length > 0 && (
          <div style={{ padding: '0 20px', marginTop: pendingNotifs.length > 0 ? 24 : 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', color: 'var(--text-3)', marginBottom: 10 }}>
              Plus tôt
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {pastNotifs.map(n => (
                <NotifCard key={n.id} notif={n} acting={acting} onAct={act} onProfile={(id) => navigate(`/profil/${id}`)} past />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function NotifCard({ notif: n, acting, onAct, onProfile, past }) {
  const bodyText = getNotifBody(n);
  const subText  = getNotifSub(n);
  const hasUser  = n.fromUser?.firstName;
  const isFollow = n.type === 'follow_request';
  const approveLabel = isFollow ? 'Suivre en retour' : 'Accepter';
  const denyLabel    = isFollow ? 'Ignorer' : 'Refuser';

  return (
    <div className="card" style={{
      padding: 14, opacity: past ? 0.75 : 1,
    }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: bodyText.actions ? 12 : 0 }}>
        <NotifAvatar user={n.fromUser} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', lineHeight: 1.4 }}>
            {hasUser && <span style={{ fontWeight: 700 }}>{n.fromUser.firstName} {n.fromUser.lastName}</span>}
            {hasUser ? ' ' : ''}{bodyText.text}
          </div>
          {subText && (
            <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{subText}</div>
          )}
          <div className="mono" style={{ fontSize: 10, color: 'var(--text-4)', marginTop: 3 }}>{timeAgo(n.createdAt)}</div>
        </div>
      </div>
      {!past && bodyText.actions && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            disabled={acting === n.id}
            onClick={() => onAct(n.id, 'approve')}
            className="btn btn-primary btn-sm"
            style={{ flex: 1, padding: '10px 0' }}>
            {acting === n.id ? '…' : approveLabel}
          </button>
          <button
            disabled={acting === n.id}
            onClick={() => onAct(n.id, 'deny')}
            style={{
              flex: 1, padding: '10px 0', borderRadius: 50, fontSize: 13, fontWeight: 600,
              background: 'var(--ink-3)', color: 'var(--text-2)', border: 'none', cursor: 'pointer',
            }}>
            {acting === n.id ? '…' : denyLabel}
          </button>
        </div>
      )}
    </div>
  );
}

function getNotifBody(n) {
  switch (n.type) {
    case 'join_request':
      return { text: `veut rejoindre ton équipe ${n.teamName}`, actions: true };
    case 'group_join_request':
      return { text: `veut rejoindre ton groupe ${n.groupName}`, actions: true };
    case 'follow_request':
      return { text: 'veut te suivre', actions: true };
    case 'join_approved':
      return { text: `a accepté ta demande pour rejoindre ${n.teamName}` };
    case 'group_invite':
      return { text: `t'a invité·e dans le groupe ${n.groupName}` };
    case 'tournament_invite':
      return { text: `t'a invité·e au tournoi ${n.tournamentName}` };
    case 'results_published':
      return { text: `Résultats publiés pour ${n.tournamentName || ''}` };
    default:
      return { text: n.message || 'nouvelle notification' };
  }
}

function getNotifSub(n) {
  if (n.subMessage) return n.subMessage;
  if (n.tournamentName && n.type === 'join_request')
    return `${n.tournamentName}${n.tournamentDate ? ' · ' + n.tournamentDate : ''}`;
  if (n.groupMemberCount != null) return `${n.groupMemberCount} membres`;
  return null;
}
